import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Mail, ChevronRight } from "lucide-react";
import SEO from "@/components/SEO";

const AboutPage = () => {
  const aboutSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Magnus Kor Talent Partners",
      "url": "https://magnuskor.com",
      "logo": "https://magnuskor.com/logo512.png",
      "foundingDate": "2024",
      "description": "Premium talent advisory firm for VC-backed SaaS startups, operating under L&D Nexus Limited (UK Company No. 16450617).",
      "parentOrganization": { "@type": "Organization", "name": "L&D Nexus Limited" },
      "address": { "@type": "PostalAddress", "streetAddress": "71-75 Shelton Street, Covent Garden", "addressLocality": "London", "postalCode": "WC2H 9JQ", "addressCountry": "GB" },
      "areaServed": ["United Kingdom", "United States", "Germany", "Singapore", "India", "Canada", "Australia"]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Who founded Magnus Kor Talent Partners?", "acceptedAnswer": { "@type": "Answer", "text": "Magnus Kor was founded by Manas Kumar with a clear vision: startup recruitment should be consultative, honest, and focused on long-term fit rather than transactional volume." }},
        { "@type": "Question", "name": "What makes Magnus Kor different from other recruitment agencies?", "acceptedAnswer": { "@type": "Answer", "text": "We combine deep SaaS startup expertise with AI-powered hiring tools, transparent pricing, and a consultative advisory approach. We focus exclusively on VC-backed SaaS startups and understand the unique challenges of each funding stage." }},
        { "@type": "Question", "name": "What is L&D Nexus Limited?", "acceptedAnswer": { "@type": "Answer", "text": "L&D Nexus Limited (UK Company No. 16450617) is the parent company of Magnus Kor Talent Partners, registered in London, United Kingdom." }}
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO 
        title="About Us - Our Story and Team"
        description="Magnus Kor Talent Partners is a talent advisory firm for VC-backed SaaS startups. Learn about our consultative approach, our team, and why founders trust us to build their core teams."
        keywords="about Magnus Kor, talent advisory team, startup recruitment agency story, SaaS hiring experts, consultative hiring approach, L&D Nexus talent division, UK recruitment agency startups"
        canonical="https://magnuskor.com/about"
        jsonLd={aboutSchema}
      />
      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-overline">About Us</span>
              <h1 className="heading-hero mt-4 mb-6">
                Talent advisory for startups
              </h1>
              <p className="text-body-lg mb-6">
                We're a new firm with a clear vision: startup recruitment should be consultative, honest, and focused on long-term fit—not transactional volume.
              </p>
              <p className="text-body">
                We help VC-backed SaaS startups build their core teams—from individual contributors to leadership—across Engineering, Product, Growth, and Customer Success.
              </p>
            </div>
            <div className="relative">
              <img loading="lazy"
                src="/images/site/unsplash-1758873268663-389c6b.jpg"
                alt="Magnus Kor talent advisors collaborating with a VC-backed SaaS startup team"
                className="w-full"
              />
              <div className="absolute -bottom-6 -right-6 w-full h-full border border-[#B8860B]/30 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Started - Founder's Voice */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-8 md:px-16">
          <span className="text-overline">Our Story</span>
          <h2 className="heading-section mt-4 mb-12">Why we started Magnus Kor</h2>
          
          <div className="space-y-8 text-body">
            <p className="text-lg leading-relaxed">
              After years of watching executive hiring go wrong—rushed decisions, misaligned expectations, candidates treated as commodities—we decided to build something different.
            </p>
            
            <p>
              The recruiting industry often optimizes for speed and volume. Firms measure success by placements made, not by outcomes achieved. Candidates get pushed into roles that aren't right for them. Companies make expensive mistakes that set them back months or years.
            </p>
            
            <p>
              We believe there's a better way. One that starts with understanding—really understanding—what a company needs before sourcing begins. One that prioritizes honest conversations over sales pitches. One that measures success by whether hires are thriving a year later, not just whether they accepted an offer.
            </p>
            
            <p>
              We're not trying to be the biggest recruiting firm. We're trying to be the one that founders trust when the hire really matters.
            </p>

            <div className="bg-white p-8 border-l-4 border-[#B8860B] mt-12">
              <p className="text-lg italic text-gray-700">
                "We'd rather do fewer searches well than many searches poorly. Every engagement should be one we're proud of."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Powered by L&D Nexus */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto px-8 md:px-16">
          <div className="text-center mb-12">
            <p className="text-[#B8860B] text-xs uppercase tracking-[0.2em] mb-4">Our Parent Company</p>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
              Powered by L&D Nexus
            </h2>
            <p className="text-gray-400 leading-relaxed max-w-2xl mx-auto">
              Magnus Kor Talent Partners operates as a specialized recruitment division of L&D Nexus, 
              registered in the United Kingdom. We combine global reach 
              with deep startup ecosystem expertise.
            </p>
          </div>

          {/* Office Card */}
          <div className="max-w-md mx-auto mb-10">
            <div className="border border-gray-800 p-6">
              <p className="text-[#B8860B] text-xs uppercase tracking-wider mb-3">UK Headquarters</p>
              <p className="text-white font-medium mb-2">L&D Nexus Limited</p>
              <p className="text-gray-500 text-sm">Company No. 16450617</p>
              <p className="text-gray-400 text-sm mt-3">
                71-75 Shelton Street, Covent Garden<br />
                London, WC2H 9JQ, United Kingdom
              </p>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-gray-800">
            <p className="text-gray-500 text-sm mb-2">office@magnuskor.com</p>
            <p className="text-gray-600 text-xs mb-4">recruitment@magnuskor.com</p>
            <div className="flex justify-center gap-4">
              <a 
                href="https://ldnexus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#D4A84B] transition-colors text-sm"
              >
                Visit L&D Nexus
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-2xl mb-16">
            <span className="text-overline">Our Values</span>
            <h2 className="heading-section mt-4">What we stand for</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Honesty Over Comfort",
                desc: "We'll tell you if a search isn't going well, if expectations are unrealistic, or if we're not the right fit. Uncomfortable truths serve everyone better than comfortable lies."
              },
              {
                title: "Quality Over Speed",
                desc: "We won't rush a search to hit a timeline if it means compromising quality. The cost of a wrong hire far exceeds the cost of waiting for the right one."
              },
              {
                title: "Understanding Before Action",
                desc: "We don't start sourcing until we truly understand your business, culture, and what success looks like. Context matters more than keywords."
              },
              {
                title: "Relationships Over Transactions",
                desc: "We're building long-term partnerships, not maximizing short-term placements. We'd rather work with fewer companies deeply than many superficially."
              },
              {
                title: "Candidates Are People",
                desc: "We treat candidates with the same respect and honesty we give clients. No ghosting, no misleading job descriptions, no wasted time."
              },
              {
                title: "Humility About Our Stage",
                desc: "We're new. We don't pretend to have experience we don't have. What we offer is fresh perspective, genuine commitment, and the energy of building something from scratch."
              }
            ].map((value, i) => (
              <div key={i} className="p-8 bg-[#FAF9F6]">
                <h3 className="font-display text-lg mb-3">{value.title}</h3>
                <p className="text-body text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Team */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-2xl mb-16">
            <span className="text-overline">The Team</span>
            <h2 className="heading-section mt-4 mb-4">Who we are</h2>
            <p className="text-body">
              We're a small team building something we believe in. No inflated titles, no fake credentials—just real people committed to doing this right.
            </p>
          </div>

          {/* Team Members - Alternating Layout */}
          <div className="space-y-16 lg:space-y-24">
            
            {/* Manas Kumar - Text Left, Image Right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center group">
              <div className="lg:order-2 relative">
                <div className="relative overflow-hidden bg-white">
                  <img loading="lazy"
                    src="/images/site/e9165d741b8c-e9165d.jpg"
                    alt="Manas Kumar - Founder & Strategic Talent Partner"
                    className="w-full aspect-[4/5] object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-full h-full border border-[#B8860B]/30 -z-10"></div>
              </div>
              <div className="lg:order-1 lg:pr-4">
                <div className="flex items-center gap-4 mb-3">
                  <h3 className="font-display text-2xl lg:text-3xl">Manas Kumar</h3>
                  <a 
                    href="https://www.linkedin.com/in/drmanaskumar/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#B8860B] transition-colors"
                    aria-label="Manas Kumar LinkedIn"
                  >
                    <Linkedin size={22} />
                  </a>
                </div>
                <p className="text-[#B8860B] text-sm font-medium tracking-wide uppercase mb-6">
                  Founder & Strategic Talent Partner
                </p>
                <p className="text-body leading-relaxed mb-4">
                  Manas is a Strategic Talent Partner working with VC-backed SaaS startups to build high-performing teams across product, growth, and customer success.
                </p>
                <p className="text-body leading-relaxed mb-4">
                  With 8+ years across recruitment, talent advisory, and HR & L&D—and experience as a startup advisor and mentor—he brings a business-first approach to hiring, aligning talent with growth, product strategy, and long-term scalability. PMP-certified and CIPD Level 5 qualified (Associate Diploma in People Management), he partners closely with founders to simplify hiring decisions and build teams that drive measurable impact.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["8+ Years Experience", "Startup Advisor & Mentor", "PMP Certified", "CIPD Level 5", "HR & L&D"].map((c) => (
                    <span key={c} className="text-xs uppercase tracking-wider text-[#B8860B] border border-[#B8860B]/30 px-3 py-1">{c}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Aleksandra - Image Left, Text Right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center group">
              <div className="relative">
                <div className="relative overflow-hidden bg-white">
                  <img loading="lazy"
                    src="/images/site/aad840575f47-aad840.jpg"
                    alt="Aleksandra Kowalewicz - Head of Talent Operations & Delivery"
                    className="w-full aspect-[4/5] object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#B8860B]/30 -z-10"></div>
              </div>
              <div className="lg:pl-4">
                <div className="flex items-center gap-4 mb-3">
                  <h3 className="font-display text-2xl lg:text-3xl">Aleksandra Kowalewicz</h3>
                  <a 
                    href="https://www.linkedin.com/in/aleksandra-kowalewicz-assoc-cipd-202025115/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#B8860B] transition-colors"
                    aria-label="Aleksandra Kowalewicz LinkedIn"
                  >
                    <Linkedin size={22} />
                  </a>
                </div>
                <p className="text-[#B8860B] text-sm font-medium tracking-wide uppercase mb-6">
                  Head of Talent Operations & Delivery
                </p>
                <p className="text-body leading-relaxed mb-4">
                  Aleksandra leads Talent Operations and Delivery, ensuring a seamless and high-quality hiring experience for both clients and candidates.
                </p>
                <p className="text-body leading-relaxed mb-4">
                  With 3+ years of experience across HR, talent advisory, and compliance—and a CIPD Level 5 Associate Diploma in People Management—she brings strong expertise across the full recruitment lifecycle, from candidate evaluation and interview process design to onboarding and compliance. She plays a key role in maintaining structured, efficient hiring processes while supporting diverse, high-growth teams with professionalism and precision.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["3+ Years Experience", "Talent Advisory", "Compliance", "HR & L&D", "CIPD Level 5"].map((c) => (
                    <span key={c} className="text-xs uppercase tracking-wider text-[#B8860B] border border-[#B8860B]/30 px-3 py-1">{c}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>

          {/* Join Us CTA */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="font-display text-lg mb-2">Want to join our team?</h3>
                <p className="text-body text-sm">We're always looking for talented recruiters, researchers, and operators.</p>
              </div>
              <Link to="/contact" className="btn-luxury btn-luxury-outline">
                Get in Touch
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/for-startups" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">For Startups</h3>
              <p className="text-gray-500 text-sm">Our services, engagement models, and how we build your core team.</p>
            </Link>
            <Link to="/for-talent" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">For Talent</h3>
              <p className="text-gray-500 text-sm">Join our network and explore roles at VC-backed SaaS startups.</p>
            </Link>
            <Link to="/contact" className="group p-6 border border-gray-200 hover:border-[#B8860B] transition-colors">
              <h3 className="font-display text-lg text-gray-900 mb-2 group-hover:text-[#B8860B]">Contact Us</h3>
              <p className="text-gray-500 text-sm">Book a consultation or get in touch about hiring needs.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
            Want to work with us?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Whether you're hiring or looking for your next role, we'd love to start a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/for-startups" className="btn-luxury btn-luxury-gold">
              I'm Hiring
              <ArrowRight size={16} />
            </Link>
            <Link to="/for-talent" className="btn-luxury border border-gray-600 text-white hover:border-[#B8860B] hover:text-[#B8860B]">
              I'm a Candidate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
